import React, { useState, useEffect } from 'react';
import { Edit, Plus, Search, Trash2 } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import VehicleModal from './vehicles/VehicleModal';


const RegisterVehicle = () => {
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    brand: ''
  });

  useEffect(() => {
    fetchBrands();
    fetchModels();
  }, [filters]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/vehicles/brands');
      let brandsData = response.data;
      
      if (filters.search) {
        brandsData = brandsData.filter(brand => 
          brand.name.toLowerCase().includes(filters.search.toLowerCase())
        );
      }
      
      setBrands(brandsData);
    } catch (error) {
      toast.error('Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const params = filters.brand ? `?brandId=${filters.brand}` : '';
      const response = await axios.get(`/api/vehicles/models${params}`);
      setModels(response.data);
    } catch (error) {
      toast.error('Failed to fetch models');
    }
  };

  const handleEdit = (item, type) => {
    setEditingItem({ ...item, type });
    setShowModal(true);
  };

  const handleAddBrand = () => {
    setEditingItem({ type: 'brand' });
    setShowModal(true);
  };

  const handleAddModel = () => {
    setEditingItem({ type: 'model' });
    setShowModal(true);
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
    
    try {
      const endpoint = type === 'brand' ? 'brands' : 'models';
      await axios.delete(`/api/vehicles/${endpoint}/${id}`);
      toast.success(`${type} deleted successfully`);
      fetchBrands();
      fetchModels();
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to delete ${type}`);
    }
  };

  const _formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingItem(null);
    fetchBrands();
    fetchModels();
  };

  const _getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'INACTIVE': return 'bg-gray-100 text-gray-800';
      case 'MAINTENANCE': return 'bg-yellow-100 text-yellow-800';
      case 'SOLD': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicle Registration</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage vehicle brands and models. These will be available in Job Card creation.
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleAddBrand}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Brand
          </button>
          <button
            onClick={handleAddModel}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Model
          </button>
        </div>
      </div>



      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search brands..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filters.brand}
            onChange={(e) => setFilters({ ...filters, brand: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Brands</option>
            {brands.map(brand => (
              <option key={brand.id} value={brand.id}>{brand.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Brands and Models */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Brands */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Vehicle Brands</h2>
            <span className="text-sm text-gray-500">{brands.length} brands</span>
          </div>
          {loading ? (
            <div className="p-6">
              <div className="animate-pulse space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
              {brands.map(brand => (
                <div key={brand.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center">
                    {brand.logoUrl ? (
                      <img
                        src={`${(window.ENV && window.ENV.REACT_APP_API_URL) || process.env.REACT_APP_API_URL || 'http://localhost:5000'}${brand.logoUrl}`}
                        alt={brand.name}
                        className="w-8 h-8 mr-3 object-contain rounded"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={`w-8 h-8 mr-3 bg-gray-200 rounded flex items-center justify-center ${brand.logoUrl ? 'hidden' : ''}`}>
                      <span className="text-xs font-medium">{brand.name.charAt(0)}</span>
                    </div>
                    <span className="font-medium">{brand.name}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(brand, 'brand')}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(brand.id, 'brand')}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Models */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Vehicle Models</h2>
            <span className="text-sm text-gray-500">{models.length} models</span>
          </div>
          <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
            {models.map(model => (
              <div key={model.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                <div className="flex items-center">
                  {model.photoUrl ? (
                    <img
                      src={`${(window.ENV && window.ENV.REACT_APP_API_URL) || process.env.REACT_APP_API_URL || 'http://localhost:5000'}${model.photoUrl}`}
                      alt={model.name}
                      className="w-8 h-8 mr-3 object-cover rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`w-8 h-8 mr-3 bg-gray-200 rounded flex items-center justify-center ${model.photoUrl ? 'hidden' : ''}`}>
                    <span className="text-xs font-medium">{model.name.charAt(0)}</span>
                  </div>
                  <div>
                    <span className="font-medium">{model.name}</span>
                    <div className="text-sm text-gray-500">{model.brand?.name}</div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(model, 'model')}
                    className="text-blue-600 hover:text-blue-800"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(model.id, 'model')}
                    className="text-red-600 hover:text-red-800"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="text-sm text-gray-600 text-center">
        {brands.length} brand{brands.length !== 1 ? 's' : ''} • {models.length} model{models.length !== 1 ? 's' : ''}
      </div>

      {/* Modals */}
      {showModal && (
        <VehicleModal
          vehicle={editingItem}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default RegisterVehicle;